import React from 'react';
import { format } from 'date-fns';
import { bookingAPI } from '../../api/bookings';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../../utils/formatters';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowPathIcon,
  UserIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const RecentBookings = ({ bookings, title = "Recent Activity", hideHeader = false }) => {
  const { isCustomer, isBusinessOwner, isStaff } = useAuth();
  const navigate = useNavigate();
  const [avatarErrors, setAvatarErrors] = React.useState({});

  const getBookingDateStr = (booking) => {
    if (booking?.booking_date_local) return booking.booking_date_local;
    if (!booking?.booking_date) return '';
    return typeof booking.booking_date === 'string'
      ? booking.booking_date.split('T')[0]
      : new Date(booking.booking_date).toISOString().split('T')[0];
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case 'confirmed': return 'text-emerald-700 bg-emerald-50 border-emerald-100';
      case 'pending': return 'text-amber-700 bg-amber-50 border-amber-100';
      case 'cancelled': return 'text-rose-700 bg-rose-50 border-rose-100';
      case 'completed': return 'text-indigo-700 bg-indigo-50 border-indigo-100';
      default: return 'text-gray-700 bg-gray-50 border-gray-100';
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await bookingAPI.updateStatus(bookingId, status);
      toast.success(`Booking ${status}!`);
      window.location.reload();
    } catch (error) {
      console.error(`Failed to ${status} booking:`, error);
      toast.error(`Failed to ${status} booking`);
    }
  };

  const handleCancelBooking = async (booking) => {
    const reason = window.prompt('Reason for cancellation?') || '';
    try {
      await bookingAPI.cancel(booking.id, reason.trim() ? reason.trim() : null);
      toast.success('Booking cancelled');
      window.location.reload();
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to cancel booking';
      toast.error(message);
    }
  };

  const handleStartBooking = async (bookingId) => {
    try {
      await bookingAPI.start(bookingId);
      toast.success('Booking started');
      window.location.reload();
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to start booking';
      toast.error(message);
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      await bookingAPI.complete(bookingId);
      toast.success('Booking completed');
      window.location.reload();
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to complete booking';
      toast.error(message);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {!hideHeader && (
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h2 className="text-xl font-black text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 font-medium">Manage your latest appointments</p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-white rounded-xl transition-all border border-transparent hover:border-gray-100 shadow-sm"
            title="Refresh bookings"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      )}
      
      <div className="divide-y divide-gray-100">
        {bookings.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClockIcon className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-500 font-bold">No bookings found</p>
            <p className="text-sm text-gray-400">Activity will appear here once customers start booking.</p>
          </div>
        ) : (
          bookings.map((booking) => (
            <div key={booking.id} className="p-6 hover:bg-gray-50/50 transition-colors group">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-black text-gray-900 group-hover:text-primary-600 transition-colors">
                      {booking.service_name}
                    </h3>
                    {booking.status !== 'completed' && (
                      <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${getStatusStyles(booking.status)}`}>
                        {booking.status}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                      <ClockIcon className="w-4 h-4 text-gray-400" />
                      <span>{format(new Date(`${getBookingDateStr(booking)}T00:00:00`), 'EEE, MMM dd')} • {(booking.start_time_local || booking.start_time)}</span>
                    </div>

                    {(isBusinessOwner || isStaff) && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 font-medium">
                        {booking.customer_profile_image && !avatarErrors[booking.id] ? (
                          <img
                            src={resolveImageUrl(booking.customer_profile_image)}
                            alt={`${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim() || 'Customer'}
                            className="w-4 h-4 rounded-full object-cover"
                            onError={() => setAvatarErrors((prev) => ({ ...prev, [booking.id]: true }))}
                          />
                        ) : (
                          <UserIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="truncate">
                          {booking.customer_first_name} {booking.customer_last_name}
                        </span>
                      </div>
                    )}

                    {isCustomer && booking.business_name && (
                      <div className="flex items-center gap-2 text-sm text-primary-600 font-bold">
                        <div className="w-4 h-4 rounded bg-primary-50 flex items-center justify-center text-[10px]">@</div>
                        <span>{booking.business_name}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-emerald-600 font-bold">
                      <CurrencyDollarIcon className="w-4 h-4" />
                      <span>{booking.currency_symbol || '$'}{booking.total_price}</span>
                    </div>

                    {booking.status === 'cancelled' && booking.cancellation_reason && (
                      <div className="md:col-span-2 text-sm text-rose-700 font-medium">
                        Reason: {booking.cancellation_reason}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex sm:flex-col gap-2 w-full sm:w-auto sm:items-end">
                  {(isBusinessOwner || isStaff) && booking.status === 'pending' && (
                    <button 
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white text-xs font-black rounded-xl hover:bg-emerald-700 transition-all shadow-sm shadow-emerald-100"
                      onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      CONFIRM
                    </button>
                  )}

                  {['confirmed', 'in_progress', 'completed'].includes(booking.status) && (
                    <button
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-xs font-black rounded-xl hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-100"
                      onClick={() => navigate(`/bookings/${booking.id}`)}
                    >
                      CHAT
                    </button>
                  )}

                  {(isBusinessOwner || isStaff) && booking.status === 'confirmed' && (
                    <button
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white text-xs font-black rounded-xl hover:bg-emerald-700 transition-all shadow-sm shadow-emerald-100"
                      onClick={() => handleStartBooking(booking.id)}
                    >
                      START
                    </button>
                  )}

                  {(isBusinessOwner || isStaff) && booking.status === 'in_progress' && (
                    <button
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-xs font-black rounded-xl hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-100"
                      onClick={() => handleCompleteBooking(booking.id)}
                    >
                      COMPLETE
                    </button>
                  )}

                  {booking.status !== 'cancelled' && booking.status !== 'completed' && booking.status !== 'in_progress' && (
                    <button
                      className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-rose-600 text-white text-xs font-black rounded-xl hover:bg-rose-700 transition-all shadow-sm shadow-rose-100"
                      onClick={() => handleCancelBooking(booking)}
                    >
                      <XCircleIcon className="w-4 h-4" />
                      CANCEL
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {bookings.length > 0 && (
        <div className="p-4 bg-gray-50/50 border-t border-gray-100 text-center">
          <button className="text-xs font-black text-gray-400 uppercase tracking-widest hover:text-primary-600 transition-colors">
            View All Activity
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentBookings;
